<template>
  <div>
    <FormLoading :loading="parentLoading" :count="8">
      <template>
        <b-form @submit.prevent="submit">
          <b-row class="margin-0">
            <b-col cols="12" class="padding-x-16">
              <Dropdown
                :title="$lang.related_exercise"
                :label="$lang.related_exercise"
                :placeholder="$lang.related_exercise"
                :value="form.exerciseSelected"
                name="exerciseSelected"
                id="exerciseSelected"
                :vuelidateObj="$v.form.exerciseSelected"
                :options="exerciseOptions"
                :loading="exerciseLoading"
                :asyncSearch="getExerciseOptions"
                @input="input"
                @focus="resetValidation"
                @blur="checkValidation"
              />
            </b-col>
            <b-col cols="12" class="padding-x-16">
              <Dropdown
                :title="$lang.related_indicator"
                :label="$lang.related_indicator"
                :placeholder="$lang.related_indicator"
                :infoTooltip="$lang.evidence_indicator_combo_tooltip"
                :value="form.indicatorSelected"
                name="indicatorSelected"
                id="indicatorSelected"
                :vuelidateObj="$v.form.indicatorSelected"
                :options="indicatorOptions"
                :loading="indicatorLoading"
                :asyncSearch="getIndicatorOptions"
                @input="input"
                @focus="resetValidation"
                @blur="checkValidation"
                @change="handleChangeIndicator"
              />
            </b-col>
            <b-col cols="12" class="padding-x-16">
              <Input
                :title="$lang.second_person_evidence"
                :label="$lang.second_person_evidence"
                :placeholder="$lang.second_person_evidence"
                :value="form.secondPersonEvidence"
                name="secondPersonEvidence"
                id="secondPersonEvidence"
                type="textarea"
                :minLength="lengthNumber3"
                :maxLength="lengthNumber256"
                :vuelidateObj="$v.form.secondPersonEvidence"
                @input="input"
                @focus="resetValidation"
                @blur="checkValidation"
              />
            </b-col>
            <b-col cols="12" class="padding-x-16">
              <Input
                :title="$lang.caption_third_person"
                :label="$lang.caption_third_person"
                :placeholder="$lang.caption_third_person"
                :value="form.captionThirdPerson"
                name="captionThirdPerson"
                id="captionThirdPerson"
                type="textarea"
                :minLength="lengthNumber3"
                :maxLength="lengthNumber256"
                :vuelidateObj="$v.form.captionThirdPerson"
                @input="input"
                @focus="resetValidation"
                @blur="checkValidation"
              />
            </b-col>
            <b-col cols="12" class="padding-x-16">
              <Input
                :title="$lang.caption_imperative"
                :label="$lang.caption_imperative"
                :placeholder="$lang.caption_imperative"
                :value="form.captionImperative"
                name="captionImperative"
                id="captionImperative"
                type="textarea"
                :minLength="lengthNumber3"
                :maxLength="lengthNumber256"
                :vuelidateObj="$v.form.captionImperative"
                :disabled="!isPositiveIndicator"
                @input="input"
                @focus="resetValidation"
                @blur="checkValidation"
              />
            </b-col>
            <b-col cols="12" class="padding-x-16 margin-bottom-16">
              <span class="font-size-12 color-complementary-300">
                {{ $lang.quality }}
                <span v-b-tooltip.hover :title="$lang.evidence_quality_helper">
                  <mdicon name="help-circle-outline" size="16" />
                </span>
              </span>
              <div class="flex-between margin-top-8">
                <ButtonCheckbox
                  v-for="(item, index) in evidenceLevel"
                  :key="index"
                  class="width-percent-100"
                  :class="{ 'margin-x-8': item.id === 2 }"
                  :text="item.caption"
                  :value="item.id"
                  :active="form.status === item.id"
                  @click="statusClicked"
                />
              </div>
            </b-col>
            <b-col cols="12" class="page-wrapper__floating-button">
              <Button
                type="submit"
                contentClass="justify-content-center"
                variant="primary"
                class="width-percent-20"
                :text="submitButtonText"
                :loading="loading"
              />
            </b-col>
          </b-row>
        </b-form>
      </template>
    </FormLoading>
  </div>
</template>

<script>
import inputMixin from "@/mixin/inputMixin.vue";
import {
  Dropdown,
  Input,
  ButtonCheckbox,
  Button,
  FormLoading,
} from "@/components/re-design/forms";
import { required, requiredIf } from "vuelidate/lib/validators";
import { pathOr } from "ramda";
import { getExerciseCombo, getIndicatorCombo } from "@/services";
import { renderEumOptions, generateIndicatorImage } from "@/shared/utils";

export default {
  mixins: [inputMixin],
  components: { Dropdown, Input, ButtonCheckbox, Button, FormLoading },
  props: {
    item: {
      type: Object,
      default: null,
    },
    parentLoading: {
      type: Boolean,
      default: false,
    },
    submitButtonText: {
      type: String,
      default: "",
    },
  },
  validations() {
    return {
      form: {
        exerciseSelected: { required },
        indicatorSelected: { required },
        secondPersonEvidence: { required },
        captionThirdPerson: { required },
        captionImperative: {
          required: requiredIf(() => this.isPositiveIndicator),
        },
        status: { required },
      },
    };
  },
  data() {
    return {
      form: {
        exerciseSelected: null,
        indicatorSelected: null,
        secondPersonEvidence: null,
        captionThirdPerson: null,
        captionImperative: null,
        status: 1,
      },
      exerciseOptions: [],
      exerciseLoading: false,
      indicatorOptions: [],
      indicatorLoading: false,
      loading: false,
    };
  },
  computed: {
    evidenceLevel() {
      return renderEumOptions("evidence_level", true);
    },
    isPositiveIndicator() {
      return pathOr(null, ["value"], this.form.indicatorSelected) === 1;
    },
  },
  watch: {
    item: {
      immediate: true,
      handler(value) {
        value && this.fetchData(value);
      },
    },
  },
  created() {
    Promise.all([this.getExerciseOptions(), this.getIndicatorOptions()]);
  },
  methods: {
    pathOr,
    generateIndicatorImage,
    statusClicked(value) {
      this.form.status = +value;
    },
    async getExerciseOptions(text = null) {
      try {
        this.exerciseLoading = true;
        const response = await getExerciseCombo(text);
        const { options } = response;
        this.exerciseOptions = options;
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.exerciseLoading = false;
      }
    },
    async getIndicatorOptions(text = null) {
      try {
        this.indicatorLoading = true;
        const response = await getIndicatorCombo(text);
        const { options } = response;
        this.indicatorOptions = options.map((item) => {
          const rightPartCaption = generateIndicatorImage(
            item.value,
            "margin-left-8"
          );
          return {
            ...item,
            rightPartCaption,
          };
        });
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.indicatorLoading = false;
      }
    },
    submit() {
      this.$v.$touch();
      if (this.$v.$anyError || this.loading) return;

      this.loading = true;

      const exerciseId = pathOr(null, ["id"], this.form.exerciseSelected);
      const indicatorId = pathOr(null, ["id"], this.form.indicatorSelected);
      const secondPersonEvidence = this.form.secondPersonEvidence;
      const captionThirdPerson = this.form.captionThirdPerson;
      const captionImperative = this.form.captionImperative;
      const status = this.form.status;

      this.$emit(
        "submit",
        exerciseId,
        indicatorId,
        secondPersonEvidence,
        captionThirdPerson,
        captionImperative,
        status,
        () => {
          this.loading = false;
        }
      );
    },
    fetchData(data) {
      this.form.exerciseSelected = { ...data.exercise };
      this.form.indicatorSelected = { ...data.indicator };
      this.form.secondPersonEvidence = data.evidence;
      this.form.captionThirdPerson = data.captionThirdPerson;
      this.form.captionImperative = data.captionImperative;
      this.form.status = data.status;
    },
    handleChangeIndicator(indicator) {
      // check if selected indicator is negative set null for caption imperative
      const isPositiveIndicator = pathOr(null, ["value"], indicator) === 1;
      if (!isPositiveIndicator) {
        this.form.captionImperative = null;
      }
    },
  },
};
</script>
